import React, { useEffect, useState } from 'react';
import { FaTimes, FaBox } from 'react-icons/fa';
import { InternalFieldName, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import {
  InvoiceProduct,
  InvoiceViewModel,
} from '../../models/Invoice/InvoiceViewModel';
import { toMask } from '../../services/currency';
import { useAuth } from '../../contexts/auth';
import api from '../../services/api';
import InvoiceStatusState from '../../enums/InvoiceStatusState';

interface InvoiceProductsProps {
  invoice: InvoiceViewModel;
  items: InvoiceProduct[];
  refreshProducts: () => void;
  removeProduct: (invoiceProduct: InvoiceProduct) => void;
}

interface InvoiceProductFormFields {
  product: number;
  quantity: number;
}

type ProductModel = {
  id: number;
  name: string;
  price: number;
  quantity: number;
};

function addServerErrors<T>(
  errors: { [P in keyof T]?: string[] },
  setError: any
) {
  return Object.keys(errors).forEach((key) => {
    setError(key as keyof T, {
      type: 'server',
      message: errors[key as keyof T]!.join('. '),
    });
  });
}

export default function InvoiceProducts({
  invoice,
  items,
  refreshProducts,
  removeProduct,
}: InvoiceProductsProps) {
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
    reset,
  } = useForm<InvoiceProductFormFields>({ mode: 'onBlur' });

  const [isLoading, setIsLoading] = useState(true);
  const [formErrors, setFormErrors] = useState<Record<string, any>>([]);
  const [products, setProducts] = useState<ProductModel[]>([]);

  const {
    user: { organization },
    isAdmin,
  } = useAuth();

  useEffect(() => {
    let shouldFocus = true;
    Object.keys(formErrors || {}).forEach((field: InternalFieldName) => {
      addServerErrors(formErrors, setError);

      shouldFocus = false;
    });
  }, [formErrors]);

  async function getProducts() {
    if (products.length === 0) {
      if (organization) {
        setIsLoading(true);
        const response = await api.get(
          `organizations/${organization.id}/products`
        );
        // TODO: Remover os que já estão adicionados
        setProducts(response.data.data);
        setIsLoading(false);
      }
    }
  }

  const onSubmit = async (data: InvoiceProductFormFields) => {
    try {
      await api.post(
        `organizations/${organization.id}/invoices/${invoice.id}/products`,
        data
      );

      setProducts([]);
      refreshProducts();
    } catch (e: any) {
      if (e?.response?.data?.errors) {
        if (e?.response?.status === 422) {
          setFormErrors(e.response.data.errors);
        } else {
          toast.error(
            e?.response?.data?.errors.reduce(
              (acc: string, error: string) => acc + error,
              ''
            )
          );
        }
      } else {
        throw e;
      }
    }
  };

  return (
    <>
      <span className='h2'>Produtos</span>
      <div className='table-responsive'>
        <table className='table'>
          <thead>
            <tr>
              <th>Nome</th>
              <th className='text-end'>Quantidade</th>
              {isAdmin && (
                <>
                  <th className='text-end'>Valor</th>
                  <th className='text-end'>Total</th>
                </>
              )}
              <th style={{ width: '20px' }}>&nbsp;</th>
            </tr>
          </thead>
          <tbody>
            {items.length > 0 ? (
              items.map((item) => (
                <tr key={item.id}>
                  <td>{item.name}</td>
                  <td className='text-end'>{item.quantity}</td>
                  {isAdmin && (
                    <>
                      <td className='text-end'>{toMask(item.price)}</td>
                      <td className='text-end'>
                        {toMask(item.quantity * item.price)}
                      </td>
                    </>
                  )}
                  <td>
                    {isAdmin &&
                      invoice.statusState !== InvoiceStatusState.Canceled &&
                      invoice.statusState !== InvoiceStatusState.Completed && (
                        <button
                          className='btn btn-sm btn-danger'
                          type='button'
                          onClick={() => removeProduct(item)}
                        >
                          <FaTimes />
                        </button>
                      )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={isAdmin ? 5 : 3}>Nenhum registro</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className='text-end'>
        {isAdmin &&
          invoice.statusState !== InvoiceStatusState.Canceled &&
          invoice.statusState !== InvoiceStatusState.Completed && (
            <button
              type='button'
              className='btn btn-secondary btn-sm'
              data-bs-toggle='modal'
              data-bs-target='#invoice-product-modal'
              onClick={() => getProducts()}
            >
              Adicionar produto <FaBox />
            </button>
          )}

        {/* Modal */}
        <div
          className='modal'
          tabIndex={-1}
          id='invoice-product-modal'
          data-bs-backdrop='static'
          data-bs-keyboard='false'
          aria-labelledby='staticBackdropLabel'
          aria-hidden='true'
        >
          <div className='modal-dialog'>
            <div className='modal-content'>
              <div className='modal-header'>
                <h5 className='modal-title'>Adicionar produto</h5>
                <button
                  type='button'
                  className='btn-close'
                  data-bs-dismiss='modal'
                  aria-label='Close'
                />
              </div>
              <div className='modal-body text-start'>
                <form
                  style={{
                    paddingTop: '1rem',
                    paddingBottom: '1rem',
                  }}
                  onSubmit={handleSubmit(onSubmit)}
                >
                  <div className='mb-3'>
                    {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                    <label className='form-label'>Selecione o produto</label>
                    <select
                      required
                      className='form-select'
                      aria-label='Selecione o produto'
                      {...register('product', { required: true })}
                    >
                      <option value=''>Selecione o produto</option>
                      {products.map((product) => (
                        <option value={product.id} key={product.id}>
                          {product.name}
                        </option>
                      ))}
                    </select>
                    {errors.product && (
                      <div className='form-text text-danger'>
                        {errors.product.message}
                      </div>
                    )}
                  </div>

                  <div className='mb-3'>
                    {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                    <label htmlFor='inputLabel' className='form-label'>
                      Quantidade
                    </label>
                    <input
                      {...register('quantity', { required: true })}
                      className='form-control'
                      placeholder='Quantidade'
                      type='number'
                      min='1'
                      step='1'
                      required
                    />
                    {errors.quantity && (
                      <div className='form-text text-danger'>
                        {errors.quantity.message}
                      </div>
                    )}
                  </div>

                  <div className='text-end mt-3'>
                    <button
                      type='submit'
                      className='btn btn-primary'
                      data-bs-dismiss='modal'
                    >
                      Adicionar
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
